import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Send, CheckCircle2, Trash2, FileText, Loader2, BellOff, Pencil } from "lucide-react";

const API = `${window.location.origin}/api`;

const todayISO = () => new Date().toISOString().slice(0, 10);

// Normalise any stored date (ISO timestamp or plain date) to YYYY-MM-DD for a
// <input type="date"> value.
const toDateInput = (value) => {
  if (!value) return todayISO();
  const s = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : todayISO();
};

const money = (n) =>
  Number(n || 0).toLocaleString("en-GB", { style: "currency", currency: "GBP" });

const computeAmount = (stage, total) => {
  if (!stage) return 0;
  if (stage.type === "fixed") return Math.round((parseFloat(stage.value) || 0) * 100) / 100;
  return Math.round(((parseFloat(stage.value) || 0) * total) / 100 * 100) / 100;
};

const PRESETS = [
  { label: "50 / 50", stages: [
    { name: "Deposit", type: "percentage", value: 50 },
    { name: "Final balance", type: "percentage", value: 50 },
  ]},
  { label: "50 / 25 / 25", stages: [
    { name: "Deposit", type: "percentage", value: 50 },
    { name: "Mid-project payment", type: "percentage", value: 25 },
    { name: "Final balance", type: "percentage", value: 25 },
  ]},
  { label: "Thirds", stages: [
    { name: "Deposit", type: "percentage", value: 33.34 },
    { name: "Mid-project payment", type: "percentage", value: 33.33 },
    { name: "Final balance", type: "percentage", value: 33.33 },
  ]},
];

/**
 * Iter 75: Payment schedule editor — embeds inside the Edit Invoice dialog.
 *
 * Lets Sandy break a single invoice into multiple stages (deposit / mid /
 * final), send a stage-specific Payment Request PDF for each, and mark
 * stages paid as the customer settles them.  The original invoice is never
 * cloned — everything stays linked to one reference (e.g. SHS-2026-026).
 */
export default function PaymentScheduleEditor({ invoiceId, authHeaders, onChanged }) {
  const [invoice, setInvoice] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyStage, setBusyStage] = useState(null);
  const [busyPayment, setBusyPayment] = useState(null);
  // Shared "pick a date" dialog used both for marking a stage paid and for
  // back-dating an already-recorded payment.
  // { title, description, date, confirmLabel, busy, onConfirm: async (dateStr) => void }
  const [dateDialog, setDateDialog] = useState(null);
  const [dateValue, setDateValue] = useState(todayISO());

  const refetch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/invoices/${invoiceId}`, authHeaders);
      setInvoice(res.data);
      setStages(res.data.payment_schedule || []);
    } catch (err) {
      console.error("PaymentScheduleEditor refetch failed:", err);
      toast.error("Couldn't load invoice");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (invoiceId) refetch(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [invoiceId]);

  const total = Number(invoice?.total || 0);
  const paidToDate = useMemo(
    () => (invoice?.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0),
    [invoice]
  );
  const outstanding = Math.max(0, Math.round((total - paidToDate) * 100) / 100);

  const sumPercent = useMemo(
    () => stages.reduce((s, st) => s + (st.type === "percentage" ? Number(st.value || 0) : 0), 0),
    [stages]
  );
  const sumFixed = useMemo(
    () => stages.reduce((s, st) => s + (st.type === "fixed" ? Number(st.value || 0) : 0), 0),
    [stages]
  );

  const applyPreset = (preset) => {
    setStages(preset.stages.map((s) => ({ ...s, id: undefined, status: "pending" })));
  };

  const addStage = () => {
    setStages([
      ...stages,
      { name: "Stage payment", type: "percentage", value: 25, status: "pending" },
    ]);
  };

  const updateStage = (idx, field, value) => {
    setStages(stages.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const removeStage = (idx) => {
    if (stages[idx]?.status === "paid") {
      toast.error("Can't delete a stage that's already paid");
      return;
    }
    setStages(stages.filter((_, i) => i !== idx));
  };

  const saveSchedule = async () => {
    if (!stages.length) {
      toast.error("Add at least one stage");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/invoices/${invoiceId}/schedule`,
        { stages: stages.map((s) => ({ ...s, value: Number(s.value || 0) })) },
        authHeaders
      );
      setStages(res.data.payment_schedule);
      toast.success("Payment schedule saved");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save schedule");
    } finally {
      setSaving(false);
    }
  };

  const requestStage = async (stage) => {
    setBusyStage(stage.id);
    try {
      const res = await axios.post(
        `${API}/admin/invoices/${invoiceId}/schedule/${stage.id}/request`,
        {},
        authHeaders
      );
      const pdfUrl = `${window.location.origin}${res.data.pdf_url}`;
      // Open in a new tab so Sandy can save / forward / share immediately.
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
      toast.success("Payment request PDF generated", {
        description: "Opened in a new tab — save or forward to the customer.",
      });
      await refetch();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate payment request");
    } finally {
      setBusyStage(null);
    }
  };

  const markStagePaid = (stage) => {
    setDateValue(todayISO());
    setDateDialog({
      kind: "mark-paid",
      title: `Mark "${stage.name}" as paid`,
      description: `Adds ${money(stage.amount)} to the invoice's paid total. Set the date the customer actually paid.`,
      confirmLabel: "Mark paid",
      onConfirm: async (dateStr) => {
        setBusyStage(stage.id);
        try {
          await axios.post(
            `${API}/admin/invoices/${invoiceId}/schedule/${stage.id}/mark-paid`,
            { method: "bank_transfer", paid_at: dateStr },
            authHeaders
          );
          toast.success(`${stage.name} marked as paid`);
          setDateDialog(null);
          await refetch();
          onChanged?.();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Failed to mark stage paid");
        } finally {
          setBusyStage(null);
        }
      },
    });
  };

  const editPaymentDate = (payment) => {
    setDateValue(toDateInput(payment.date));
    setDateDialog({
      kind: "edit-date",
      title: "Edit payment date",
      description: `${money(payment.amount)}${payment.stage_name ? ` · ${payment.stage_name}` : ""}. This is the date the customer paid — it updates the Payment Request PDF.`,
      confirmLabel: "Save date",
      onConfirm: async (dateStr) => {
        setBusyPayment(payment.id);
        try {
          await axios.patch(
            `${API}/admin/invoices/${invoiceId}/payments/${payment.id}`,
            { date: dateStr },
            authHeaders
          );
          toast.success("Payment date updated");
          setDateDialog(null);
          await refetch();
          onChanged?.();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Failed to update payment date");
        } finally {
          setBusyPayment(null);
        }
      },
    });
  };

  const deletePayment = async (payment) => {
    if (!window.confirm(
      `Delete this payment of ${money(payment.amount)}?\n\nThis removes it from the invoice's paid total${payment.stage_id ? " and resets the linked stage back to pending" : ""}. This cannot be undone.`
    )) return;
    setBusyPayment(payment.id);
    try {
      await axios.delete(
        `${API}/admin/invoices/${invoiceId}/payments/${payment.id}`,
        authHeaders
      );
      toast.success("Payment deleted");
      await refetch();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete payment");
    } finally {
      setBusyPayment(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading schedule…
      </div>
    );
  }
  if (!invoice) return null;

  return (
    <div className="space-y-4" data-testid="payment-schedule-editor">
      {/* Totals strip */}
      <div className="grid grid-cols-3 gap-3 p-3 rounded-lg bg-slate-50 border">
        <div>
          <p className="text-xs text-muted-foreground">Project total</p>
          <p className="text-lg font-semibold" data-testid="ps-total">{money(total)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Paid to date</p>
          <p className="text-lg font-semibold text-emerald-700" data-testid="ps-paid">{money(paidToDate)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Outstanding</p>
          <p className="text-lg font-semibold text-amber-700" data-testid="ps-outstanding">{money(outstanding)}</p>
        </div>
      </div>

      {/* Recorded payments — lets Sandy delete duplicate / erroneous records */}
      {(invoice.payments || []).length > 0 && (
        <div className="border rounded-lg" data-testid="recorded-payments-section">
          <div className="px-3 py-2 bg-slate-100 text-xs font-medium text-slate-700 rounded-t-lg">
            Recorded payments ({invoice.payments.length})
          </div>
          <ul className="divide-y">
            {invoice.payments.map((p, idx) => (
              <li
                key={p.id || `pay-${idx}`}
                className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                data-testid={`recorded-payment-${idx}`}
              >
                <div className="min-w-0">
                  <span className="font-semibold tabular-nums">{money(p.amount)}</span>
                  <span className="text-muted-foreground ml-2">
                    {p.stage_name ? `${p.stage_name} · ` : ""}
                    {(p.type || "payment").replace(/_/g, " ")}
                    {p.method ? ` · ${String(p.method).replace(/_/g, " ")}` : ""}
                  </span>
                  <span className="text-muted-foreground ml-2 hidden sm:inline">
                    {p.date ? new Date(p.date).toLocaleDateString("en-GB") : ""}
                  </span>
                </div>
                <div className="inline-flex gap-1 shrink-0">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => editPaymentDate(p)}
                    disabled={busyPayment === p.id}
                    data-testid={`edit-payment-date-${idx}`}
                    title="Edit the date this payment was made"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => deletePayment(p)}
                    disabled={busyPayment === p.id}
                    data-testid={`delete-payment-${idx}`}
                    title="Delete this payment record"
                  >
                    {busyPayment === p.id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Trash2 className="h-3.5 w-3.5 text-destructive" />}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Preset buttons */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs text-muted-foreground mr-1">Quick preset:</span>
        {PRESETS.map((p) => (
          <Button
            key={p.label}
            type="button"
            size="sm"
            variant="outline"
            onClick={() => applyPreset(p)}
            data-testid={`ps-preset-${p.label.replace(/\s|\//g, '')}`}
          >
            {p.label}
          </Button>
        ))}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={addStage}
          data-testid="ps-add-stage"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add stage
        </Button>
      </div>

      {/* Stages table */}
      {stages.length === 0 ? (
        <div className="text-center py-6 text-muted-foreground text-sm border rounded-lg">
          No stages yet — pick a preset above or click <em>Add stage</em>.
        </div>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left p-2">#</th>
                <th className="text-left p-2 min-w-[160px]">Stage</th>
                <th className="text-left p-2 w-32">Type</th>
                <th className="text-right p-2 w-24">Value</th>
                <th className="text-right p-2 w-28">Amount</th>
                <th className="text-left p-2 w-32">Due</th>
                <th className="text-left p-2 w-28">Status</th>
                <th className="text-right p-2 w-56">Actions</th>
              </tr>
            </thead>
            <tbody>
              {stages.map((s, idx) => {
                const computed = computeAmount(s, total);
                const isPaid = s.status === "paid";
                const isRequested = s.status === "requested";
                return (
                  <tr key={s.id || `new-${idx}`} className="border-t" data-testid={`ps-stage-${idx}`}>
                    <td className="p-2 text-muted-foreground">{idx + 1}</td>
                    <td className="p-2">
                      <Input
                        value={s.name || ""}
                        disabled={isPaid}
                        onChange={(e) => updateStage(idx, "name", e.target.value)}
                        className="h-8"
                      />
                    </td>
                    <td className="p-2">
                      <select
                        value={s.type || "percentage"}
                        disabled={isPaid}
                        onChange={(e) => updateStage(idx, "type", e.target.value)}
                        className="h-8 px-2 border rounded text-sm w-full bg-white"
                      >
                        <option value="percentage">Percent (%)</option>
                        <option value="fixed">Fixed (£)</option>
                      </select>
                    </td>
                    <td className="p-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={s.value ?? ""}
                        disabled={isPaid}
                        onChange={(e) => updateStage(idx, "value", e.target.value)}
                        className="h-8 text-right"
                      />
                    </td>
                    <td className="p-2 text-right tabular-nums">{money(s.amount ?? computed)}</td>
                    <td className="p-2">
                      <Input
                        type="date"
                        value={s.due_date || ""}
                        disabled={isPaid}
                        onChange={(e) => updateStage(idx, "due_date", e.target.value)}
                        className="h-8"
                      />
                    </td>
                    <td className="p-2">
                      {isPaid && <Badge className="bg-emerald-100 text-emerald-800">Paid</Badge>}
                      {isRequested && <Badge className="bg-amber-100 text-amber-800">Requested</Badge>}
                      {!isPaid && !isRequested && <Badge variant="outline">Pending</Badge>}
                    </td>
                    <td className="p-2 text-right">
                      <div className="inline-flex gap-1">
                        {!isPaid && s.id && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => requestStage(s)}
                            disabled={busyStage === s.id}
                            data-testid={`ps-request-${idx}`}
                            title={isRequested ? "Re-generate the PDF" : "Generate a Payment Request PDF for this stage"}
                          >
                            {busyStage === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
                              isRequested ? <FileText className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
                            <span className="ml-1 hidden sm:inline">{isRequested ? "PDF" : "Send"}</span>
                          </Button>
                        )}
                        {!isPaid && s.id && (
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => markStagePaid(s)}
                            disabled={busyStage === s.id}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`ps-mark-paid-${idx}`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            <span className="ml-1 hidden sm:inline">Paid</span>
                          </Button>
                        )}
                        {!isPaid && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => removeStage(idx)}
                            data-testid={`ps-remove-${idx}`}
                            title="Remove this stage"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t bg-slate-50">
                <td colSpan={3} className="p-2 text-right text-xs text-muted-foreground">Totals</td>
                <td className="p-2 text-right text-xs text-muted-foreground tabular-nums">
                  {sumPercent ? `${sumPercent.toFixed(2)}%` : ""}
                  {sumPercent && sumFixed ? " + " : ""}
                  {sumFixed ? money(sumFixed) : ""}
                </td>
                <td className="p-2 text-right font-semibold tabular-nums">
                  {money(stages.reduce((s, st) => s + (st.amount ?? computeAmount(st, total)), 0))}
                </td>
                <td colSpan={3} className="p-2 text-right">
                  {sumPercent > 100 && (
                    <span className="text-xs text-destructive flex items-center justify-end gap-1">
                      <BellOff className="h-3 w-3" /> Stages exceed 100%
                    </span>
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          onClick={saveSchedule}
          disabled={saving || stages.length === 0}
          data-testid="ps-save-schedule"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save schedule
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Tip: changes to stage <em>amount</em> or <em>value</em> only take effect after <strong>Save schedule</strong>.
        Sending or marking a stage paid is instant.
      </p>

      <Dialog open={!!dateDialog} onOpenChange={(o) => { if (!o) setDateDialog(null); }}>
        <DialogContent className="max-w-sm" data-testid="payment-date-dialog">
          <DialogHeader>
            <DialogTitle>{dateDialog?.title}</DialogTitle>
            <DialogDescription>{dateDialog?.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="ps-date-input">Date paid</Label>
            <Input
              id="ps-date-input"
              type="date"
              value={dateValue}
              onChange={(e) => setDateValue(e.target.value)}
              data-testid="payment-date-input"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDateDialog(null)}
              data-testid="payment-date-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => dateDialog?.onConfirm?.(dateValue)}
              disabled={!dateValue || busyStage || busyPayment}
              data-testid="payment-date-confirm"
            >
              {(busyStage || busyPayment) ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {dateDialog?.confirmLabel || "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
